import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Textarea,
  Stack,
  Typography,
  Sheet,
} from '@mui/joy';
import { useNavigate } from 'react-router';
import { FC, FormEvent, JSX, useState } from 'react';
import { Dropdown, Input, Loader } from '@consolidocs-ui/components';
import { PageBreadcrumbs } from '@consolidocs-ui/components';
import {
  FieldErrorType,
  handleApiErrors,
  useToast,
  sortAlphanumeric,
} from '@consolidocs-ui/utils';
import { ErrorType, IndustryFormType } from '@consolidocs-ui/types';
import { useCreateIndustryMutation } from '@consolidocs-ui/data-access/IndustryApi';
import { useGetContractsQuery } from '@consolidocs-ui/data-access/ContractApi';
import { ContractType } from '@consolidocs-ui/types';

export const industryInitialState: IndustryFormType = {
  code: '',
  name: '',
  description: '',
  contract_types: [],
};

const CreateIndustry: FC = (): JSX.Element => {
  const [industry, setIndustry] =
    useState<IndustryFormType>(industryInitialState);
  const [errors, setErrors] = useState<FieldErrorType>({});
  const [createIndustry, { isLoading }] = useCreateIndustryMutation();
  const { data: contractTypes } = useGetContractsQuery();
  const navigate = useNavigate();
  const [toastTrigger] = useToast();

  const handleChange = (
    field: keyof IndustryFormType,
    value: string | string[]
  ) => {
    setIndustry({ ...industry, [field]: value });
    setErrors({ ...errors, [field]: '' });
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const newErrors: FieldErrorType = {};

    if (!industry.name.trim()) newErrors.name = 'Industry name is required';
    if (!industry.code.trim()) newErrors.code = 'Industry code is required';

    if (Object.keys(newErrors).length > 0) {
      return setErrors(newErrors);
    }

    try {
      await createIndustry(industry).unwrap();
      toastTrigger({
        message: `Industry created successfully`,
        type: 'success',
      });
      setIndustry(industryInitialState);
      navigate('/industries');
    } catch (err) {
      handleApiErrors<IndustryFormType>(
        err,
        setErrors,
        (message: string, color: 'success' | 'danger') =>
          toastTrigger({ message, type: color }),
        (err as ErrorType)?.data?.status_message || 'Failed to create industry'
      );
    }
  };

  return (
    <Box sx={{ flex: 1, width: '100%' }}>
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 9995,
          bgcolor: '#f5f5f5',
          // Cancel the .content wrapper's top padding (p-4) so the bar sticks
          // flush to the top instead of drifting down before it engages.
          mt: { md: -2 },
          pt: { xs: 1, md: 2 },
          pb: 1,
        }}
      >
        <PageBreadcrumbs items={[{ label: 'Create Industry' }]} />
      </Box>

      <Sheet
        variant="outlined"
        sx={{
          width: { xs: '100%', sm: '90%', md: '800px' },
          maxWidth: '800px',
          mx: 'auto',
          p: { xs: 2, sm: 3, md: 4 },
          borderRadius: 'md',
          boxShadow: 'sm',
          borderColor: 'neutral.outlinedBorder',
          marginTop: { xs: '12px', md: '20px' },
        }}
      >
        <Typography level="h4" mb={2}>
          Create Industry
        </Typography>

        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{ mx: 'auto', mt: 4 }}
        >
          <Stack spacing={2}>
            <FormControl required>
              <FormLabel>Industry Code</FormLabel>
              <Input
                value={industry.code}
                onChange={(e) => handleChange('code', e.target.value)}
                placeholder="e.g., IT, FIN"
                required
                error={!!errors['code']}
                errorMessage={errors['code']}
                size="sm"
              />
            </FormControl>

            <FormControl required>
              <FormLabel>Industry Name</FormLabel>
              <Input
                value={industry.name}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="e.g., Information Technology"
                required
                error={!!errors['name']}
                errorMessage={errors['name']}
                size="sm"
              />
            </FormControl>

            <FormControl>
              <FormLabel>Description</FormLabel>
              <Textarea
                minRows={3}
                value={industry.description}
                onChange={(e) => handleChange('description', e.target.value)}
                placeholder="Optional description"
                size="sm"
              />
            </FormControl>

            <Dropdown
              label="Contract Types"
              options={sortAlphanumeric(
                contractTypes?.data.map((type: ContractType) => ({
                  label: type.name,
                  value: type.id,
                })),
                ({ label }) => label
              )}
              multiple
              value={industry.contract_types}
              onChange={(_, newValue) =>
                handleChange('contract_types', newValue)
              }
              error={errors.contract_types}
            />
            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button
                type="button"
                variant="plain"
                color="neutral"
                onClick={() => navigate('/industries')}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="solid"
                color="primary"
                loading={isLoading}
                loadingIndicator={<Loader size="sm" />}
              >
                Save
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Sheet>
    </Box>
  );
};

export default CreateIndustry;
